import {
  ACTIVITY_TYPE,
  CustomerApiForActivities,
  DedicatedApi,
  ICustomerListVariables,
} from "@/api-client";
import ApiManagementSectionHeader from "@/components/api-management/ApiManagementSectionHeader.component";
import {
  Button,
  ButtonSizeEnum,
  ButtonThemeEnum,
} from "@/components/shared/Button.component";
import { Loader } from "@/components/shared/Loader.component";
import { SelectedDataExtractor } from "@/components/shared/table/selected-data-extractor";
import { TableEditingState } from "@/components/shared/table/table-editing-state";
import { TableSelectionState } from "@/components/shared/table/table-selection-state";
import Table from "@/components/shared/table/Table.component";
import {
  ICellDataContent,
  IItem,
  ITableField,
} from "@/components/shared/table/types";
import Tabs, { Tab } from "@/components/shared/Tabs.component";
import {
  IQueryListPublishRunsByApiArgs,
  SortOrderType,
} from "@/generated/types";
import { BContainer, BRow } from "@/lib/typed-bootstrap";
import UserPermissions from "@/mixins/UserPermissions.mixin";
import { BaseFieldTypeEnum, IApiState } from "@/models";
import { ApiSchema } from "@/models/api-schema";
import { GraphqlApiSchemaHandler } from "@/models/api-schema/graphql-api-schema-handler";
import {
  IActivityResponseMappableProps,
  getTablenameForType,
  parseActivity,
} from "@/models/customer-apis/activity";
import { IPublishRunItem, watchPublishRunsByApi } from "@/models/publish-run";
import { isNotNullable } from "@/utils";
import { ListDataManager } from "@/utils/list-data-manager";
import { ProductNameEnum } from "@graphapi-io/products";
import { ApolloClient } from "apollo-boost";
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";

const getInitialActivityListVariables = (): ICustomerListVariables => ({
  limit: 50,
  nextToken: null,
  filter: undefined,
  sortOrder: SortOrderType.DESC,
  sortRange: undefined,
});

@Component
export default class ApiDetailsDeployments extends UserPermissions {
  @Prop({ required: true }) value: IApiState;
  @Prop({ required: true }) apiClient: ApolloClient<unknown>;
  @Prop({ required: true }) apiSchemaHandler: GraphqlApiSchemaHandler | null;

  $refs!: {
    publishRunsTable: HTMLFormElement;
  };

  private currentTabIndex = 0;

  private selectedDataExtractor = new SelectedDataExtractor(
    [],
    [],
    true,
    false,
  );

  private tableSelectionState = Vue.observable(
    new TableSelectionState(true, false),
  );

  private get tableEditingState() {
    return Vue.observable(new TableEditingState(this.listQueryActivityFields));
  }

  private get apiId(): string {
    return this.$route.params.apiId;
  }

  private get activitiesListDataManager() {
    return Vue.observable(
      new ListDataManager<
        {
          items: { [key: string]: string | number | null | undefined }[];
          nextToken?: string | null;
        },
        ICustomerListVariables
      >(
        this.customerApiForActivities.watchActivities.bind(
          this.customerApiForActivities,
        ),
      ),
    );
  }

  private get customerApi() {
    return new DedicatedApi(
      this.apiClient,
      this.schemaFieldTypes,
      new ApiSchema(this.value, this.schemaFieldTypes),
    );
  }

  private get customerApiForActivities() {
    return new CustomerApiForActivities(this.customerApi);
  }

  private get publishRunTableFields(): ITableField[] {
    const publishRunFields = [
      { key: "createdAt", label: this.$t("global_date").toString() },
      { key: "schemaVersion", label: this.$t("global_version").toString() },
      { key: "status", label: this.$t("global_status").toString() },
      this.canPublishApi
        ? {
            key: "publish",
            label: "",
            formatter: (content: ICellDataContent, item?: IItem) => (
              <Button
                theme={ButtonThemeEnum.secondary}
                size={ButtonSizeEnum.xs}
                class="mx-2"
                onClick={() =>
                  this.handleRepublishClick((item?.id ?? "").toString())
                }
              >
                {this.$t("logs_republish")}
              </Button>
            ),
          }
        : undefined,
    ].filter(isNotNullable);

    return [...(publishRunFields ?? "")];
  }

  private get activityTableFields(): ITableField[] {
    return [
      { key: "createdAt", label: this.$t("global_date").toString() },
      {
        key: "activity",
        label: this.$t("global_activity").toString(),
        formatter: (content: ICellDataContent, item?: IItem) => (
          <i18n path={content}>
            <template slot="table">
              <strong>
                {getTablenameForType(
                  (item?.recordType ?? "") as string,
                  this.value.types,
                )}
              </strong>
            </template>
          </i18n>
        ),
      },
      {
        key: "recordId",
        label: this.$t("global_entry_id").toString(),
        formatter: (content: ICellDataContent, item?: IItem) => (
          <span style="font-family: monospace; font-size: 14px;">
            {item?.recordId}
          </span>
        ),
      },
    ].filter(isNotNullable);
  }

  @Watch("apiSchemaHandler.inProgress", { immediate: true })
  protected onApiSchemaHandlerInitialized() {
    if (this.apiSchemaHandler && !this.apiSchemaHandler?.inProgress) {
      this.activitiesListDataManager.subscribeToList(
        getInitialActivityListVariables(),
      );
      this.publishRunsListDataManager.subscribeToList({
        apiId: this.apiId,
        limit: 50,
        nextToken: null,
      });
    }
  }

  get hasActivitiesField() {
    return this.schemaFieldTypes["Activity"] !== undefined;
  }

  private get activityFields(): string[] {
    return (
      this.schemaFieldTypes[ACTIVITY_TYPE]?.fields
        ?.filter(
          (field) =>
            field.args &&
            field.args?.length === 0 &&
            field.name !== "id" &&
            field.name !== "key" &&
            field.name !== "etag" &&
            field.name !== "updatedAt",
        )
        .map((field) => field.name) ?? []
    );
  }

  private get listQueryActivityFields() {
    return this.activityFields.map((field) => ({
      key: field,
      label: field,
      type: BaseFieldTypeEnum.String,
      readOnly: true,
    }));
  }

  private get schemaFieldTypes() {
    return this.apiSchemaHandler?.inProgress || !this.apiSchemaHandler
      ? {}
      : this.apiSchemaHandler?.schemaFieldTypes;
  }

  private get activityData() {
    return this.activitiesListDataManager.data.map(
      (item) =>
        parseActivity(
          item as unknown as IActivityResponseMappableProps,
        ) as unknown as IItem,
    );
  }

  private get products() {
    return this.value.products ?? [];
  }

  private handleRepublishClick(id: string) {
    this.$router.push({
      name: "project-republish-view",
      params: { apiId: this.apiId, versionId: id },
    });
  }

  private onTabIndexChange(index: number) {
    this.currentTabIndex = index;
  }

  private publishRunsListDataManager = Vue.observable(
    new ListDataManager<
      { items: IPublishRunItem[]; nextToken?: string | null },
      IQueryListPublishRunsByApiArgs
    >(watchPublishRunsByApi),
  );

  private get publishRuns() {
    return this.publishRunsListDataManager.data;
  }

  private publishRunsSelectedDataExtractor = new SelectedDataExtractor(
    [],
    [],
    false,
    false,
  );

  private get publishRunsTableSelectionState() {
    return new TableSelectionState(true, false);
  }

  private get publishRunsTableEditingState() {
    return Vue.observable(new TableEditingState(this.publishRunTableFields));
  }

  render() {
    return (
      <BContainer class="api__view-setup" fluid>
        <Tabs
          class="datamodel-tabs"
          currentTabIndex={this.currentTabIndex}
          onTabIndexChange={this.onTabIndexChange}
        >
          <Tab
            title={this.$t("api_details_section_title_activities").toString()}
            id="activities"
          >
            <ApiManagementSectionHeader
              description={this.$t(
                "api_details_section_description_activities",
                {
                  timeframe: this.products.includes(ProductNameEnum.pro_bundle)
                    ? this.$t("api_details_section_activities_timeframe_pro")
                    : this.$t(
                        "api_details_section_activities_timeframe_default",
                      ),
                },
              ).toString()}
            />
            <Table
              ref="table"
              items={this.activityData}
              hasMoreItems={
                !!this.activitiesListDataManager.listVariables?.nextToken
              }
              fields={this.activityTableFields}
              withSelectionColumn
              tableSelectionState={this.tableSelectionState}
              tableEditingState={this.tableEditingState}
              selectedDataExtractor={this.selectedDataExtractor}
              onPaginationChange={
                this.activitiesListDataManager.onPaginationChange
              }
              paginationState={this.activitiesListDataManager.paginationState}
            >
              {!this.hasActivitiesField ? (
                <div>{this.$t("api_details_activites_publishing_hint")}</div>
              ) : this.activitiesListDataManager.isLoading ||
                !this.schemaFieldTypes ? (
                <Loader data-testid="tableLoader" />
              ) : null}
            </Table>
          </Tab>
          <Tab
            title={this.$t("api_details_section_title_logs").toString()}
            id="publish-runs"
          >
            <ApiManagementSectionHeader
              description={this.$t(
                "api_details_section_description_logs",
              ).toString()}
            />
            <BRow class="default__sub-section">
              <Table
                items={this.publishRuns}
                fields={this.publishRunTableFields}
                tableSelectionState={this.publishRunsTableSelectionState}
                tableEditingState={this.publishRunsTableEditingState}
                selectedDataExtractor={this.publishRunsSelectedDataExtractor}
                onPaginationChange={
                  this.publishRunsListDataManager.onPaginationChange
                }
                paginationState={
                  this.publishRunsListDataManager.paginationState
                }
                hasMoreItems={
                  !!this.publishRunsListDataManager.listVariables?.nextToken
                }
              >
                {this.publishRunsListDataManager.isLoading ? (
                  <Loader data-testid="tableLoader" />
                ) : null}
              </Table>
            </BRow>
          </Tab>
        </Tabs>
      </BContainer>
    );
  }
}
